import React, { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { FaPrint, FaShareFromSquare, FaPlus } from "react-icons/fa6";

// Design Components
import Modal from "../../organisms/Modal/Modal";
import Logo from "../../molecules/Logo/Logo";
import Print from "../Print/Print";
import Button from "../../atoms/Button/Button";
import Icon from "../../atoms/Icon/Icon";
import { IconValues } from "../../atoms/Icon/Icon.types";
import Divider from "../../atoms/Divider/Divider";
import { ButtonSizes, ButtonTypes } from "../../atoms/Button/Button.types";
import StickyContainer from "../../atoms/StickyContainer/StickyContainer";
import Container from "../../atoms/Container/Container";

// StudentReport Component Stuff
import {
  StudentReportProps,
  StudentSkill,
  Strategy,
  SelfeLesson,
} from "./StudentReport.types";
import { getIcon } from "./utils/getIcon";

import "./StudentReport.scss";
import "../../tokens/alignment.scss";

const StudentReport: React.FC<StudentReportProps> = ({ report }) => {
  const student = report.student;
  const skills = report.skills;
  const mathStrategies = report.mathStrategies;
  const languageArtsStrategies = report.languageArtsStrategies;
  const selfeLessonsData = report.selfeLessonsData;

  const componentRef = useRef<HTMLDivElement>(null);

  const [showModal, setShowModal] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (
    <div
      data-testid="StudentReport"
      className="mp-templates-studentreport--main"
    >
      {report && (
        <>
          <Modal show={showModal} handleClose={() => setShowModal(false)}>
            Share
          </Modal>
          <StickyContainer backgroundColor="#fff" top={"5.2em"}>
            <div>
              <div className="web-header">
                <div>
                  <h1>STUDENT LEARNER PROFILE</h1>
                </div>
                <div className="button-container">
                  <Button
                    primary={true}
                    label={
                      <span className="icon-text">
                        <FaPrint />
                        <p>Print</p>
                      </span>
                    }
                    size={ButtonSizes.icon}
                    type={ButtonTypes.neutral}
                    onClick={handlePrint}
                  />
                  {/* <Button
                    primary={true}
                    label={
                      <span className="icon-text">
                        <FaShareFromSquare />
                        <p>Share</p>
                      </span>
                    }
                    size={ButtonSizes.icon}
                    type={ButtonTypes.neutral}
                    onClick={() => setShowModal(true)}
                  /> */}
                </div>
              </div>
              <div className="student-container">
                <h2>
                  {student.first_name} {student.last_name}&nbsp;
                </h2>
                <div className="student-info">
                  <h5>Test Date:&nbsp;</h5>
                  <p>{new Date(student.mpTestDate).toLocaleDateString()}</p>
                </div>
                {student.school && student.school.length > 0 && (
                  <div className="student-info">
                    <h5>School:&nbsp;</h5>
                    <p>{student.school}</p>
                  </div>
                )}
              </div>
            </div>
          </StickyContainer>
          <Container>
            <h2 className="section-header mp-blue">About MindPrint</h2>
            <div className="paragraph-wrapper">
              <P1 />
              <P2 />
              <MainBox skills={skills} />
              <P5 />
              <div className="strategies-container">
                <LangStrategies langStrats={languageArtsStrategies} />
                <MathStrategies mathStrats={mathStrategies} />
              </div>
            </div>
            <Divider />
            <div className="elessons-wrapper">
              <h2 className="section-header mp-blue">
                Personalized Recommendations for{" "}
                {student.first_name || student.last_name || "your student"}
              </h2>
              <P6 />
              <ELessons lessons={selfeLessonsData} />
            </div>
            <Divider />
            <Glossary />
          </Container>

          <Print ref={componentRef}>
            <div className="mp-templates-studentreport--main-print">
              <div className="page">
                <PrintHeader />
                <PrintStudentInfo
                  name={`${student.first_name} ${student.last_name}`}
                  date={student.mpTestDate}
                  school={student.school}
                />
                <h2 className="print-margin mp-blue">About MindPrint</h2>
                <div className="paragraph-wrapper">
                  <P1 />
                  <P2 />
                  <MainBox skills={skills} />
                  <P5 />
                  <div className="strategies-container">
                    <LangStrategies langStrats={languageArtsStrategies} />
                    <MathStrategies mathStrats={mathStrategies} />
                  </div>
                </div>
              </div>
              <div className="page">
                <div className="elessons-wrapper">
                  <h2>
                    Personalized Recommendations for{" "}
                    {student.first_name || student.last_name || "your student"}
                  </h2>
                  <P6 />
                  <ELessons lessons={selfeLessonsData} />
                </div>{" "}
              </div>
              <div className="page">
                <div className="glossary-wrapper">
                  <h2 className="mp-blue">Glossary of Terms</h2>
                  <ComplexReasoning />
                  <LongTermMemory />
                </div>
              </div>
              <div className="page">
                <div className="glossary-wrapper">
                  <ExecutiveFunctions />
                </div>
              </div>
            </div>
          </Print>
        </>
      )}
    </div>
  );
};

export default StudentReport;

const PrintHeader = () => {
  return (
    <div className="print-header">
      <h2>STUDENT LEARNER PROFILE</h2>
      <Logo />
    </div>
  );
};

const PrintStudentInfo: React.FC<{
  name: string;
  date: string;
  school: string;
}> = ({ name, date, school }) => {
  return (
    <div className="student-container">
      <h2>{name}</h2>
      <div className="student-info">
        <h5>Test Date:&nbsp;</h5>
        <p>{new Date(date).toLocaleDateString()}</p>
      </div>
      {school && school.length > 0 && (
        <div className="student-info">
          <h5>School:&nbsp;</h5>
          <p>{school}</p>
        </div>
      )}
    </div>
  );
};

const MainBox: React.FC<{ skills: Array<StudentSkill> }> = ({ skills }) => {
  return (
    <div className="main-box">
      <div className="image-text-wrapper">
        <div className="image-wrapper">
          <img
            src={"https://mindprint-general.s3.amazonaws.com/lightbulb.png"}
          />
        </div>
        <div className="text-wrapper">
          <h2>MY MINDPRINT PROFILE</h2>

          <div className="paragraph-wrapper">
            <P3 />
            <P4 />
          </div>
        </div>
      </div>
      <div className="skill-card-container">
        {skills.map((skill, index) => {
          return (
            <SkillCard
              key={`student-report-skill-card-${index}`}
              skill={skill}
            />
          );
        })}
      </div>
    </div>
  );
};

const SkillCard: React.FC<{
  skill: StudentSkill;
}> = ({ skill }) => {
  return (
    <div className="skill-card-wrapper">
      <div
        className={`skill-card${skill.highlight ? " highlight" : " standard"}`}
      >
        {/* SKILL TITLE */}
        <div className="title-container">
          <h3>{skill.title}</h3>
          <div className={`subtitle${skill.highlight ? "" : " standard"}`}>
            {skill.subtitle}
          </div>
        </div>

        {/* SUB-SKILL LIST */}
        <div className="subskill-container">
          <div className="subskill-list">
            {skill.subskills.map((subskill, index) => {
              return (
                <div key={`subskill-${index}`} className="subskill-wrapper">
                  <div
                    className={`title${subskill?.order === 1 ? " bold" : ""}`}
                  >
                    {subskill?.title}
                  </div>
                  <div>
                    <Icon value={getIcon(subskill?.title)} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* TOP STRATEGY */}
          <div className="top-strategy-wrapper">
            <div className={`title${skill.highlight ? "" : " standard"}`}>
              Top Strategy
            </div>
            <div className="icon-link">
              {/* Executive Functions skills card must use student's
                  weakest subskill (last array item) */}
              <a
                href={
                  skill.title === "Executive Functions"
                    ? skill.subskills[skill.subskills.length - 1]?.link
                    : skill.subskills[0]?.link
                }
                target="_blank"
              >
                {skill.title === "Executive Functions"
                  ? skill.subskills[skill.subskills.length - 1]?.linkTitle
                  : skill.subskills[0]?.linkTitle}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LangStrategies: React.FC<{ langStrats: Strategy[] }> = ({
  langStrats,
}) => {
  return (
    <div className="strategy">
      <div className="strategy-title">
        <Icon value={IconValues.books} />
        <h4>READING & WRITING</h4>
      </div>
      <div className="strategy-items">
        {langStrats.map((strategy) => {
          return (
            <div key={strategy.title} className="icon-link">
              <FaPlus />
              <a href={strategy.url} target="_blank">
                {strategy.title}
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MathStrategies: React.FC<{ mathStrats: Strategy[] }> = ({
  mathStrats,
}) => {
  return (
    <div className="strategy">
      <div className="strategy-title">
        <Icon value={IconValues.functionIcon} />
        <h4>MATH & SCIENCE</h4>
      </div>
      <div className="strategy-items">
        {mathStrats.map((strategy) => {
          return (
            <div key={strategy.title} className="icon-link">
              <FaPlus />
              <a href={strategy.url} target="_blank">
                {strategy.title}
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ELessons: React.FC<{ lessons: SelfeLesson[] }> = ({ lessons }) => {
  return (
    <table className="lessons-table">
      <thead>
        <tr className="header-row">
          <th className="subject-header">Subject</th>
          <th className="strategies-header">Recommended Strategies</th>
        </tr>
      </thead>
      <tbody>
        {lessons.map((lesson) => {
          return (
            <tr key={lesson.id} className="body-row">
              <td className="subject-body">{lesson.title}</td>
              <td className="strategies-body">
                {lesson.referenceList.map((reference) => {
                  return (
                    <div key={reference.title} className="icon-link">
                      <FaPlus />
                      <a href={reference.link} target="_blank">
                        {reference.title}
                      </a>
                    </div>
                  );
                })}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const Glossary: React.FC = () => {
  return (
    <div className="glossary-wrapper">
      <h2 className="mp-blue">Glossary of Terms</h2>
      <ComplexReasoning />
      <LongTermMemory />
      <ExecutiveFunctions />
    </div>
  );
};

const ComplexReasoning: React.FC = () => {
  return (
    <div className="term-card-wrapper">
      {/* CATEGORY TITLE */}
      <div className="category-header">
        <h3>COMPLEX REASONING</h3>
        <p>
          Complex Reasoning is your ability to understand and analyze new
          information, and solve problems. You take in new information in a
          variety of ways, most often reading, hearing, seeing, and visualizing.
        </p>
      </div>

      {/* TERMS */}
      <div className="terms-container">
        <div className="icon-title">
          <Icon value={IconValues.verbalReasoning} />
          <h5>Verbal Reasoning</h5>
        </div>
        <p>
          Understanding and applying what you read and hear. Biggest impact: All
          subjects, since most academic topics depend on reading, listening, and
          writing, but most important in reading comprehension and word
          problems.
        </p>
        <div className="icon-title">
          <Icon value={IconValues.visualAbstractReasoning} />
          <h5>Visual-Abstract Reasoning</h5>
        </div>
        <p>
          Understanding non-language-based information, including images,
          numbers, shapes, patterns, and challenging concepts. Biggest impact:
          High school math and science; also main idea and inference questions
          in English and social studies.
        </p>
        <div className="icon-title">
          <Icon value={IconValues.visualSpatialReasoning} />
          <h5>Visual-Spatial Reasoning</h5>
        </div>
        <p>
          Visualizing and understanding how objects relate in space. Biggest
          impact: Geometry, graphs and charts, science, keeping math and writing
          work neat, reading efficiency, grammar, art and design, especially
          anything in 3-D; Speed on standardized tests.
        </p>
      </div>
    </div>
  );
};

const LongTermMemory: React.FC = () => {
  return (
    <div className="term-card-wrapper">
      {/* CATEGORY TITLE */}
      <div className="category-header">
        <h3>MEMORY</h3>
        <p>
          Memory is how efficiently you memorize new information and recall it
          over time. Weaker memory can be offset by spaced practice and
          effective strategy use.
        </p>
      </div>

      {/* TERMS */}
      <div className="terms-container">
        <div className="icon-title">
          <Icon value={IconValues.verbalMemory} />
          <h5>Verbal Memory</h5>
        </div>
        <p>
          Remembering what you read or hear. Biggest impact: Subject-specific
          vocabulary, remembering details of what you read, remembering the
          specifics of class discussions.
        </p>
        <div className="icon-title">
          <Icon value={IconValues.visualMemory} />
          <h5>Visual Memory</h5>
        </div>
        <p>
          Remembering formulas, figures, and pictures or other
          non-language-based information. Biggest impact: Geometry, math
          formulas, science graphs and charts, geography, colors, and patterns.
        </p>
      </div>
    </div>
  );
};

const ExecutiveFunctions: React.FC = () => {
  return (
    <div className="term-card-wrapper">
      {/* CATEGORY TITLE */}
      <div className="category-header">
        <h3>EXECUTIVE FUNCTIONS & SPEED</h3>
        <p>
          Executive Functions & Speed refer to how accurately, efficiently, and
          systematically you can complete your assignments (regardless of how
          well you know and understand the information).
        </p>
      </div>

      {/* TERMS */}
      <div className="terms-container">
        <div className="icon-title">
          <Icon value={IconValues.attention} />
          <h5>Attention</h5>
        </div>
        <p>
          Sustaining focus to work accurately and efficiently. Biggest impact:
          Topics you find less interesting, completing work in the allotted
          time, avoiding careless mistakes, participating throughout class,
          getting started and finishing your homework.
        </p>
        <div className="icon-title">
          <Icon value={IconValues.workingMemory} />
          <h5>Working Memory</h5>
        </div>
        <p>
          Juggling information in your head for multi-step problem solving or
          tasks. Biggest impact: Following directions, avoiding careless
          mistakes, keeping organized, mental math, note taking, remembering
          ideas long enough to participate in class discussion or write them
          down.
        </p>
        <div className="icon-title">
          <Icon value={IconValues.flexibleThinking} />
          <h5>Flexible Thinking</h5>
        </div>
        <p>
          Adapting to unfamiliar information or situations. Biggest impact:
          Understanding "trick" questions, making decisions, adapting in new
          situations, working in groups, creative problem solving.
        </p>
        <div className="icon-title">
          <Icon value={IconValues.fasterSpeed} />
          <h5>Speed</h5>
        </div>
        <p>
          Responding by balancing speed and accuracy. Biggest impact: Answering
          too fast and making careless errors, responding slower and having
          difficulty finishing in time or keeping up with the class.
        </p>
      </div>
    </div>
  );
};

const P1 = () => (
  <div>
    Everyone learns differently. <em>MindPrint skills</em> (or cognitive skills)
    are the skills your brain uses to help you understand, solve problems,
    remember, plan and pay attention. These skills work together to determine{" "}
    <em>HOW you learn best</em> in all subjects of school, whether you're
    writing, creating a video, or taking a math test.
  </div>
);

const P2 = () => (
  <div>
    No one is equally strong in every skill. Understanding your{" "}
    <em>stronger skills</em> will help you work more efficiently so you can
    spend less time studying, and more time on things you enjoy. Using the
    strategies recommended below will help you use your strengths most
    efficiently.
  </div>
);

const P3 = () => (
  <div>
    Below is your MindPrint Profile. It lists your skills in each category in
    order of strength.{" "}
    <em>
      Your strongest skill is listed first in each category. You also have a
      strongest overall category. It's the highlighted tall box below
    </em>
    . When you are faced with a challenge you will always want to start with
    your strengths. The strategies below will help you use your strengths.
  </div>
);

const P4 = () => (
  <div>
    The last skill in each category could be a “blocker” and make learning
    challenging at times, but you can always find ways to use your strengths to
    succeed. Since Executive Functions can be the biggest blocker, in that
    category we give a strategy to help minimize any challenge.
  </div>
);

const P5 = () => (
  <div>
    <i>
      <b>Subject Checklist:</b> Use these checklists to help you do your best
      work.
    </i>
  </div>
);

const P6 = () => (
  <div>
    Here's a prioritized list of strategies to get you started by subject and
    skill based on your MindPrint Profile. You can find many more by searching
    your Toolbox.
  </div>
);
